import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, TrendingUp, LogOut,
  Menu, ChevronRight, DollarSign, Target, Activity,
  ArrowUpRight, LayoutDashboard, Briefcase, FileText,
  BarChart3, Workflow, CheckCircle2, Lock, ArrowRight, Loader2,
  Shield, PieChart, Store, Building2
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_CONFIG = {
  builder: { label: 'Builder', color: 'emerald', icon: Workflow, desc: 'Optimize your current workflow', tagline: 'Zero income risk. Pure skill amplification.' },
  side_operator: { label: 'Side Operator', color: 'violet', icon: Users, desc: 'Serve 1-2 external clients', tagline: 'Controlled expansion. Side income, not replacement.' },
  open_market: { label: 'Open Market', color: 'amber', icon: TrendingUp, desc: 'Full marketplace access', tagline: 'Diversification over scale. Stability through optionality.' },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [tierStatus, setTierStatus] = useState(null);
  const [incomeMetrics, setIncomeMetrics] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    fetchData();
    // Show welcome modal for new users
    if (user && !user.onboarding_complete) {
      setShowWelcome(true);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes, tierRes, incomeRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/leads`),
        axios.get(`${API}/tier/status`),
        axios.get(`${API}/income/metrics`),
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
      setTierStatus(tierRes.data);
      setIncomeMetrics(incomeRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const baseNav = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Building2, label: 'Businesses', path: '/businesses' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: Activity, label: 'Agent Monitor', path: '/agents' },
    { icon: DollarSign, label: 'Revenue', path: '/revenue' },
    { icon: Shield, label: 'Operations', path: '/operations' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: BarChart3, label: 'Analytics', path: '/analytics' },
  ];
  if (user && user.is_admin) {
    baseNav.push({ icon: Shield, label: 'Admin: Beta', path: '/admin/beta-users' });
  }
  const navItems = baseNav;

  const getStatusColor = (status) => {
    const colors = { contacted: 'bg-violet-500/20 text-violet-400', responded: 'bg-amber-500/20 text-amber-400', negotiating: 'bg-blue-500/20 text-blue-400', closed: 'bg-emerald-500/20 text-emerald-400', lost: 'bg-red-500/20 text-red-400' };
    return colors[status] || 'bg-white/10 text-muted-foreground';
  };

  const tierConfig = TIER_CONFIG[tierStatus?.current_tier || 'builder'];
  const concentrationColor = (incomeMetrics?.concentration_score || 100) >= 75 ? 'text-red-400' : (incomeMetrics?.concentration_score || 100) >= 40 ? 'text-amber-400' : 'text-emerald-400';

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <HSLogo size={32} />
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}>
                  <Icon className="w-5 h-5" />{item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); toast.success('Logged out'); }} className="w-full justify-start text-muted-foreground hover:text-white" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white" data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold">Dashboard</h1>
            </div>
            <div className="flex items-center gap-3">
              {tierStatus && (
                <span className={`text-xs font-mono px-2 py-1 border ${
                  tierConfig.color === 'emerald' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                  tierConfig.color === 'violet' ? 'bg-violet-500/10 text-violet-400 border-violet-500/20' :
                  'bg-amber-500/10 text-amber-400 border-amber-500/20'
                }`} data-testid="tier-badge">
                  {tierConfig.label.toUpperCase()}
                </span>
              )}
              <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="text-muted-foreground hover:text-white h-9 px-3" data-testid="header-logout-btn">
                <LogOut className="w-4 h-4 mr-1" /> <span className="hidden sm:inline text-sm">Sign Out</span>
              </Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : (
            <>
              {/* De-Risk Narrative Banner */}
              <div className="bg-[#0a0a0a] border border-white/5 p-6" data-testid="narrative-banner">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{tierConfig.tagline}</p>
                    <p className="text-sm text-muted-foreground mt-1">{incomeMetrics?.messaging?.subtext || "Business owners diversify revenue. Skilled professionals can diversify income."}</p>
                  </div>
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-6">
                {/* Income Concentration Score */}
                <div className="bg-[#0a0a0a] border border-white/5 p-6" data-testid="concentration-section">
                  <div className="flex items-center gap-2 mb-4">
                    <PieChart className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Income Concentration</h3>
                  </div>
                  <div className="flex items-end gap-3 mb-3">
                    <span className={`font-mono text-4xl font-bold ${concentrationColor}`}>{incomeMetrics?.concentration_score || 100}%</span>
                    <span className={`text-xs font-mono uppercase mb-1 ${concentrationColor}`}>{incomeMetrics?.concentration_label || 'High Risk'}</span>
                  </div>
                  <div className="w-full h-2 bg-white/5 mb-4">
                    <div className={`h-full transition-all ${
                      (incomeMetrics?.concentration_score || 100) >= 75 ? 'bg-red-500' :
                      (incomeMetrics?.concentration_score || 100) >= 40 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`} style={{ width: `${incomeMetrics?.concentration_score || 100}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground italic border-t border-white/5 pt-3">
                    "Security does not come from one employer. Security comes from optionality."
                  </p>
                  <Button onClick={() => navigate('/income-calculator')} variant="outline" size="sm" className="mt-3 w-full border-white/10 text-xs rounded-sm" data-testid="income-calc-link">
                    <Shield className="w-3 h-3 mr-1" /> Income Insurance Calculator
                  </Button>
                </div>

                {/* Income Stacking Simulation */}
                <div className="bg-[#0a0a0a] border border-white/5 p-6 lg:col-span-2" data-testid="stacking-section">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-muted-foreground" />
                      <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Income Stacking Simulation</h3>
                    </div>
                    <span className="text-[10px] text-muted-foreground">Based on your workflow value</span>
                  </div>

                  {incomeMetrics?.monthly_per_client > 0 ? (
                    <div className="space-y-3">
                      {(incomeMetrics?.stacking_simulation || []).map((scenario, i) => (
                        <div key={i} className="flex items-center gap-4">
                          <div className="w-24 text-xs text-muted-foreground">{scenario.label}</div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono text-sm text-white">${scenario.monthly_side_income.toLocaleString()}<span className="text-muted-foreground text-xs">/mo</span></span>
                              <span className={`text-[10px] font-mono ${
                                scenario.concentration_score >= 60 ? 'text-amber-400' : 'text-emerald-400'
                              }`}>{scenario.concentration_score}% concentrated</span>
                            </div>
                            <div className="w-full h-1.5 bg-white/5 flex">
                              <div className="h-full bg-emerald-500/30 transition-all" style={{ width: `${100 - scenario.concentration_score}%` }} />
                              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(scenario.clients * 15, 100 - scenario.concentration_score)}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground italic pt-2 border-t border-white/5">
                        "You are not replacing your paycheck. You are reducing dependence on a single source."
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-sm text-muted-foreground mb-3">Build your first workflow to see income projections</p>
                      <Button onClick={() => navigate('/workflow')} variant="outline" size="sm" className="border-emerald-500/20 text-emerald-400 rounded-sm">
                        Map Your Workflow <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Tier Progression */}
              {tierStatus && (
                <div className="bg-[#0a0a0a] border border-white/5 p-6" data-testid="tier-section">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-bold">Your Progression</h2>
                    {tierStatus.income_projection?.with_5_clients > 0 && (
                      <div className="text-right">
                        <p className="text-[10px] text-muted-foreground uppercase">Potential (5 clients)</p>
                        <p className="font-mono text-lg font-bold text-emerald-400">${tierStatus.income_projection.with_5_clients.toLocaleString()}<span className="text-xs text-muted-foreground">/mo</span></p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-0 mb-4">
                    {['builder', 'side_operator', 'open_market'].map((tier, i) => {
                      const tc = TIER_CONFIG[tier];
                      const TIcon = tc.icon;
                      const isCurrent = tierStatus.current_tier === tier;
                      const isPast = ['builder', 'side_operator', 'open_market'].indexOf(tierStatus.current_tier) > i;
                      return (
                        <React.Fragment key={tier}>
                          {i > 0 && <div className={`flex-1 h-0.5 ${isPast ? 'bg-emerald-500' : 'bg-white/10'}`} />}
                          <div className={`flex flex-col items-center ${isCurrent ? '' : 'opacity-50'}`}>
                            <div className={`w-10 h-10 flex items-center justify-center rounded-full mb-1 ${isPast || isCurrent ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-muted-foreground'}`}>
                              {isPast ? <CheckCircle2 className="w-5 h-5" /> : isCurrent ? <TIcon className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
                            </div>
                            <span className={`text-[10px] font-mono uppercase ${isCurrent ? 'text-emerald-400' : 'text-muted-foreground'}`}>{tc.label}</span>
                            <span className="text-[9px] text-muted-foreground max-w-[100px] text-center mt-0.5 hidden sm:block">{tc.desc}</span>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {tierStatus.next_tier && tierStatus.progress && (
                    <div className="bg-black/30 border border-white/5 p-4">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Progress to {TIER_CONFIG[tierStatus.next_tier]?.label}</p>
                      <div className="space-y-2">
                        {Object.entries(tierStatus.progress).map(([key, val]) => (
                          <div key={key} className="flex items-center gap-3">
                            <div className={`w-5 h-5 flex items-center justify-center ${val.met ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                              {val.met ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-3 h-3 border border-white/20 rounded-full" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between text-xs"><span className="capitalize">{key}</span><span className="font-mono">{val.current}/{val.required}</span></div>
                              <div className="w-full h-1 bg-white/5 mt-1"><div className={`h-full transition-all ${val.met ? 'bg-emerald-500' : 'bg-white/20'}`} style={{ width: `${Math.min((val.current / val.required) * 100, 100)}%` }} /></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Leads', value: stats?.total_leads || 0, icon: Target, color: 'emerald' },
                  { label: 'Active Pitches', value: stats?.active_pitches || 0, icon: Activity, color: 'violet' },
                  { label: 'Deals Closed', value: stats?.deals_closed || 0, icon: Briefcase, color: 'amber' },
                  { label: 'Revenue', value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'emerald' },
                ].map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <div key={i} className="bg-[#0a0a0a] border border-white/5 p-5 hover:border-emerald-500/30 transition-colors" data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}>
                      <div className={`w-10 h-10 flex items-center justify-center mb-3 ${stat.color === 'emerald' ? 'text-emerald-400 bg-emerald-500/10' : stat.color === 'violet' ? 'text-violet-400 bg-violet-500/10' : 'text-amber-400 bg-amber-500/10'}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <p className="text-2xl font-bold font-mono">{stat.value}</p>
                      <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Quick Actions */}
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { title: 'Map Your Workflow', description: 'Extract and optimize your daily tasks', icon: Workflow, path: '/workflow', color: 'emerald' },
                  { title: 'Demand Intelligence', description: 'Find high-demand service opportunities', icon: BarChart3, path: '/demand', color: 'violet' },
                  { title: 'Find & Pitch Jobs', description: 'Search live job boards and generate pitches', icon: Search, path: '/jobs', color: 'amber' },
                ].map((action, i) => {
                  const Icon = action.icon;
                  return (
                    <button key={i} onClick={() => navigate(action.path)} className="bg-[#0a0a0a] border border-white/5 p-6 text-left hover:border-emerald-500/30 transition-all hover:-translate-y-1 group" data-testid={`quick-action-${i}`}>
                      <Icon className={`w-8 h-8 mb-4 ${action.color === 'emerald' ? 'text-emerald-400' : action.color === 'violet' ? 'text-violet-400' : 'text-amber-400'}`} />
                      <h4 className="font-bold mb-1 group-hover:text-emerald-400 transition-colors">{action.title}</h4>
                      <p className="text-sm text-muted-foreground">{action.description}</p>
                    </button>
                  );
                })}
              </div>

              {/* Narrative Footer */}
              <div className="border border-white/5 p-6 text-center" data-testid="narrative-footer">
                <p className="text-sm text-muted-foreground italic">
                  "AI does not replace your skill. It multiplies your capacity."
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {incomeMetrics?.messaging?.principle || "Employment is a single contract. Fractionalization is a portfolio."}
                </p>
              </div>

              {/* Recent Leads */}
              <div className="bg-[#0a0a0a] border border-white/5">
                <div className="flex items-center justify-between p-6 border-b border-white/5">
                  <h3 className="font-bold">Recent Leads</h3>
                  <Link to="/crm" className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1">View all <ChevronRight className="w-4 h-4" /></Link>
                </div>
                <div className="divide-y divide-white/5">
                  {recentLeads.length > 0 ? recentLeads.map((lead, i) => (
                    <div key={i} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                      <div><p className="font-medium">{lead.company}</p><p className="text-sm text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</p></div>
                      <span className={`px-2 py-1 text-xs font-mono uppercase ${getStatusColor(lead.status)}`}>{lead.status}</span>
                    </div>
                  )) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No leads yet. Start by mapping your workflow!</p>
                      <Button onClick={() => navigate('/workflow')} variant="outline" className="mt-4 border-white/10" data-testid="empty-leads-cta">Start Building</Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Welcome Modal */}
      <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md text-center">
          <div className="py-4 space-y-4">
            <HSLogo size={48} className="mx-auto" />
            <h2 className="text-xl font-bold">Welcome to HumanScaled</h2>
            <p className="text-muted-foreground text-sm">Build your first revenue-producing workflow in under 5 minutes. No code. No complexity.</p>
            <Button onClick={() => { setShowWelcome(false); navigate('/onboarding'); }} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="welcome-start">
              Start Setup <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <button onClick={() => setShowWelcome(false)} className="text-xs text-muted-foreground hover:text-white">Skip for now</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
